import {Platform} from '@angular/cdk/platform';
import {Location, NgClass} from '@angular/common';
import {AfterViewInit, Component, DOCUMENT, inject, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {ExtendedModule} from '@angular/flex-layout';
import {MatMomentDateModule} from '@angular/material-moment-adapter';
import {MatBottomSheetModule} from '@angular/material/bottom-sheet';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule, MatIconRegistry} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {HammerModule, Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, Route, Router} from '@angular/router';
import {AccountManagementProviderService} from '@modules/account-management';
import {TypologyLabel} from '@modules/activities/core/typologies/typology.label';
import {ActivityEditionConfigurationService} from '@modules/activity-edition/core/features/activity-edition-configuration.service';
import {AssignationInitializerService} from '@modules/assignation/core/services/assignation-initializer.service';
import {AuthenticationService, UserDataEntity} from '@modules/authentication';
import {AuthorizationService} from '@modules/authorization';
import {BdMenuService} from '@modules/bdtool/core/bd-menu.service';
import {BrowserTestService} from '@modules/browser-test';
import {CommunicationCenterService} from '@modules/communication-center';
import {CorpusMenuService} from '@modules/corpus/core/services/corpus-menu.service';
import {GraphHumanumInitService} from '@modules/graph-humanum/core/services/graph-humanum-init.service';
import {GroupsManagementInitializerService} from '@modules/groups-management/core/services/groups-management-initializer.service';
import {LicensesService} from '@modules/groups-management/core/services/licenses.service';
import {LessonEditionConfigurationService} from '@modules/lesson-edition/core/features/lesson-edition-configuration.service';
import {LrsModule} from '@modules/lrs';
import {LangChangeEvent, TranslateModule, TranslateService} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {Angulartics2GoogleGlobalSiteTag, Angulartics2Matomo} from 'angulartics2';
import {navigation} from 'app/navigation/navigation';
import {FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule} from 'fuse-core/components';
import {MobileRedirectionModalComponent} from 'fuse-core/components/mobile-redirection-modal/mobile-redirection-modal.component';
import {FuseNavigationService} from 'fuse-core/components/navigation/navigation.service';
import {FuseSidebarService} from 'fuse-core/components/sidebar/sidebar.service';
import {NewsModule} from 'fuse-core/news/news.module';
import {SnackbarNewsService} from 'fuse-core/news/snackbar/snackbar-news.service';
import {AccessibilityService} from 'fuse-core/services/accessibility.service';
import {GlobalConfigurationService} from 'fuse-core/services/global-configuration.service';
import {LayoutConfigService} from 'fuse-core/services/layout-config.service';
import {FuseSplashScreenService} from 'fuse-core/services/splash-screen.service';
import {FuseTranslationLoaderService} from 'fuse-core/services/translation-loader.service';
import {FuseSharedModule} from 'fuse-core/shared.module';
import {FuseNavigation, LayoutConfig} from 'fuse-core/types';
import {capitalize} from 'lodash-es';
import {OctopusConnectService} from 'octopus-connect';
import posthog from 'posthog-js';
import {Subject} from 'rxjs';
import {timer} from 'rxjs/internal/observable/timer';
import {filter, map, take, takeUntil, tap} from 'rxjs/operators';
import {EventService} from 'shared/event.service';
import {FreemiumPremiumCompareAccessComponent} from 'shared/fremium-premium/freemium-premium-compare-access/freemium-premium-compare-access.component';
import {FreemiumPremiumConfirmTrialAccessComponent} from 'shared/fremium-premium/freemium-premium-confirm-trial-access/freemium-premium-confirm-trial-access.component';
import {FreemiumPremiumModalComponent} from 'shared/fremium-premium/freemium-premium-modal/freemium-premium-modal.component';
import {FreemiumPremiumReverseTrialComponent} from 'shared/fremium-premium/freemium-premium-reverse-trial/freemium-premium-reverse-trial.component';
import {FreemiumPremiumService} from 'shared/fremium-premium/freemium-premium.service';
import {LanguageService} from 'shared/language.service';
import {AnyLinkType} from 'shared/pretty-easy-navigation/models';
import {SharedTranslateModule} from 'shared/shared-translate.module';
import {isMobile} from 'shared/utils/navigator';
import {IsAllowedBySettings} from '../../../../src/app/guards';
import {isAuthorizedByFreemiumPremiumGuard} from '../../../../src/app/guards/is-authorized-by-freemium-premium.guard';
import {HorizontalLayout1Module} from '../../../../src/app/layout/horizontal/layout-1/layout-1.module';
import {LayoutModule} from '../../../../src/app/layout/layout.module';
import {VerticalLayout1Module} from '../../../../src/app/layout/vertical/layout-1/layout-1.module';
import {VerticalLayout2Module} from '../../../../src/app/layout/vertical/layout-2/layout-2.module';
import {VerticalLayout3Module} from '../../../../src/app/layout/vertical/layout-3/layout-3.module';
import {BodyClassService} from '../../../../src/app/navigation/body-class.service';
import {
    accessibiActivate,
    brand,
    brandTitle,
    customLayoutConfig,
    defaultLanguage,
    defaultMetaDescription,
    GAdsTracking,
    GAdsTrackingIDFromSettings,
    GATracking,
    GTMTracking,
    GTMTrackingIDFromSettings,
    isMobileAlert,
    langs,
    matomoTracking,
    matomoTrackingIDFromSettings,
    MetaPixelTracking,
    MetaPixelTrackingIDFromSettings,
    mobileUrlRedirection,
    SnapchatTracking,
    SnapchatTrackingIDFromSettings,
    tarteaucitronConf,
    trackingIDFromSettings,
    twitterTracking,
    youTubeTracking,
} from '../../../../src/app/settings';
import {TralaTranslationLoaderService} from '../../../../src/app/trala-translation-loader.service';
import {TtsModule} from '../../../../src/app/tts/tts.module';
import {environment} from '../../../../src/environments/environment';
import {FreemiumWhitelistRoutes} from './freemium-whitelist-routes';
import {NavigationService} from './navigation/navigation.service';
import {ServiceInstantiator} from './service-instantiator.class';

declare var tarteaucitron: any;
declare global {
    interface Window {
        tarteaucitronForceLanguage: string;
        opera: string;
        _paq: any;
    }
}

@Component({
    selector: 'app-root',
    imports: [
    ExtendedModule,
    FuseProgressBarModule,
    HorizontalLayout1Module,
    NewsModule,
    NgClass,
    TranslateModule,
    VerticalLayout1Module,
    VerticalLayout2Module,
    VerticalLayout3Module,
    SharedTranslateModule,
    MatMomentDateModule,
    MatExpansionModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSidenavModule,
    MatDialogModule,
    MatBottomSheetModule,
    FuseSharedModule,
    FuseSidebarModule,
    FuseThemeOptionsModule,
    TtsModule,
    LayoutModule,
    LrsModule,
    HammerModule
],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    title = 'i-love-english';

    lang: string;
    browserLang: string;
    themeColor = 'red';
    layoutConfig: LayoutConfig;
    navigation: FuseNavigation[];
    public isSnackbarRecallButtonConfigActive = false;
    public isAllowedSkipNavigation = false;
    public brand: string = brand;
    public globalConfigurationService = inject(GlobalConfigurationService);
    private tarteaucitronConf: object = tarteaucitronConf;
    private brandTitle: string = brandTitle;
    private defaultMetaDescription: any = defaultMetaDescription;
    private trackingID: string = trackingIDFromSettings;
    private matomoTrackingID: string = matomoTrackingIDFromSettings;
    private GAdsTrackingID: string = GAdsTrackingIDFromSettings;
    private GTMTrackingID: string = GTMTrackingIDFromSettings;
    private MetaPixelTrackingID: string = MetaPixelTrackingIDFromSettings;
    private SnapchatTrackingID: string = SnapchatTrackingIDFromSettings;
    private GATracking: boolean = GATracking;
    private matomoTracking: boolean = matomoTracking;
    private youTubeTracking: boolean = youTubeTracking;
    private twitterTracking: boolean = twitterTracking;
    private GAdsTracking: boolean = GAdsTracking;
    private GTMTracking: boolean = GTMTracking;
    private MetaPixelTracking: boolean = MetaPixelTracking;
    private SnapchatTracking: boolean = SnapchatTracking;
    private accessibi = accessibiActivate;
    private _unsubscribeAll: Subject<void>;
    private languageService = inject(LanguageService);
    private route = inject(ActivatedRoute);
    private isRouteAccessible = inject(IsAllowedBySettings);
    private trackingInitialized = false;
    private router = inject(Router);
    private communicationCenter = inject(CommunicationCenterService);
    private authService = inject(AuthenticationService);
    private authorizationService = inject(AuthorizationService);
    private dialog = inject(MatDialog);
    private _layoutConfigService = inject(LayoutConfigService);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _fuseSidebarService = inject(FuseSidebarService);
    private _fuseSplashScreenService = inject(FuseSplashScreenService);
    private _fuseTranslationLoaderService = inject(FuseTranslationLoaderService);
    private _translateService = inject(TranslateService);
    private _platform = inject(Platform);
    private translationLoader = inject(TralaTranslationLoaderService);
    private _serviceInstantiator = inject(ServiceInstantiator);
    private browserTestService = inject(BrowserTestService);
    private matIconRegistry = inject(MatIconRegistry);
    private connector = inject(OctopusConnectService);
    private snackbarNews = inject(SnackbarNewsService);
    private usersProvider = inject(AccountManagementProviderService);
    private titleService = inject(Title);
    private angularticsGA = inject(Angulartics2GoogleGlobalSiteTag);
    private angularticsMatomo = inject(Angulartics2Matomo);
    private renderer = inject(Renderer2);
    private meta = inject(Meta);
    private corpusMenuService = inject(CorpusMenuService); // Inject CorpusMenuService to initialize it
    private groupsInitializer = inject(GroupsManagementInitializerService);
    private bdMenuService = inject(BdMenuService);
    private graphHumanumInitService = inject(GraphHumanumInitService);
    private assignationInitService = inject(AssignationInitializerService);
    private freemiumPremiumService = inject(FreemiumPremiumService);
    private location = inject(Location);
    private licensesService = inject(LicensesService);
    private navigationService = inject(NavigationService);
    private bodyClassService = inject(BodyClassService);
    private eventService = inject(EventService);
    private accessibilityService = inject(AccessibilityService);
    private lessonEditionConfig = inject(LessonEditionConfigurationService)
    private activityEditionConfig = inject(ActivityEditionConfigurationService)
    private userData: UserDataEntity;

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.navigationService.setMenu('guest'); // Set the initial menu to 'trainer'
        this._unsubscribeAll = new Subject<void>();
        this.initNavigation();
        this.initLangManagement();
        this.initMobilePlatformManagement();
        this.initLayoutConfigManagement();
        this.initBodyClassManagement();
        this.groupsInitializer.initialize();
        this.assignationInitService.initialize();
        this.corpusMenuService.initialize();
        this.graphHumanumInitService.initialize();
        // add default meta description if exists
        if (this.defaultMetaDescription && this.defaultMetaDescription[this._translateService.currentLang]) {
            this.meta.addTag({
                name: 'description',
                content: this.defaultMetaDescription[this._translateService.currentLang]
            });
        }

        this.communicationCenter
            .getRoom('corpus')
            .getSubject('displayEmptyCorpusHelper')
            .next(true);

        this.communicationCenter
            .getRoom('freemium-premium')
            .getSubject('freemiumPremiumTrial')
            .pipe(
                take(1),
                tap(() => this.triggerFreemiumTrialModal())
            )
            .subscribe()

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((userData: UserDataEntity) => {
                if (userData) {
                    this.userData = userData;
                    if (this.authService.isAtLeastTrainer()) {
                        this.communicationCenter
                            .getRoom('ftux')
                            .getSubject('ftuxInitialized')
                            .next(userData);
                        this.communicationCenter
                            .getRoom('freemium-premium')
                            .getSubject('freemiumPremiumFeatureEnabled')
                            .next(true);

                        !this.trackingInitialized ? this.initTracking() : null;
                        posthog.init(
                            environment.posthog.posthogApiKey,
                            {
                                api_host: environment.posthog.apiHost,
                                person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
                                defaults: '2025-05-24',
                                autocapture: true,
                                persistence: 'memory',
                                disable_session_recording: true,

                            }
                        );
                        posthog.stopSessionRecording();
                        setTimeout(() => {
                            posthog.identify(
                                userData?.id.toString(), {
                                    email: userData.get('email')
                                }
                            );
                        }, 500);
                    }

                    Sentry.setUser({email: userData.get('email')});
                    this.postAuthentication(userData);
                } else {
                    !this.trackingInitialized ? this.initTracking() : null;
                    posthog.reset(true);
                    this.postLogout();
                }
            });

        this.communicationCenter
            .getRoom('filters')
            .getSubject('floatLabel')
            .next('auto');


        this.communicationCenter
            .getRoom('freemium-premium')
            .getSubject('access-premium-display-compare-modal')
            .pipe(
                tap(() => {
                    const dialogRef = this.dialog.open(
                        FreemiumPremiumCompareAccessComponent, {
                            panelClass: 'freemium-premium-compare-access-wrapper',
                        }
                    );
                    dialogRef.afterClosed().subscribe((result) => {
                        if (result) {
                            // User chose to upgrade to premium
                            window.location.href = environment.externalUrls(brand) + `&email=${encodeURIComponent(this.userData.get('email'))}`;
                        }
                    });
                })
            )
            .subscribe();
        this.communicationCenter
            .getRoom('freemium-premium')
            .getSubject('access-premium-display-reverse-trial-modal')
            .pipe(
                tap(() => {
                    const dialogRef = this.dialog.open(
                        FreemiumPremiumReverseTrialComponent, {
                            autoFocus: 'first-header',
                            panelClass: 'freemium-premium-reverse-trial-wrapper',
                        }
                    );
                    dialogRef.afterClosed().subscribe((result) => {
                        if (result) {
                            this.connector.createEntity('customer-success/lesson-streak/claim').pipe(take(1))
                                .subscribe({
                                    next: () => {
                                        // licence créée → reload

                                        const dialogRef = this.dialog.open(
                                            FreemiumPremiumConfirmTrialAccessComponent, {
                                                autoFocus: 'first-header',
                                                panelClass: 'freemium-premium-confirm-trial-access-wrapper',
                                        }
                                    );
                                    dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
                                        this.updateFreemiumPremiumMode(this.userData, true);
                                    });
                                },
                                error: (err) => {
                                    console.error('Erreur création licence', err);
                                    throw err
                                }
                            });
                        }
                    });
                })
            )
            .subscribe();
    }

    ngOnInit(): void {
        this.accessibilityService.init();
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                // if in whitelist array and is not authenticated
                if (!this.authService.isAuthenticated) {
                    const whiteListUrls = ['login', 'register', 'forgot-password'];
                    for (let i = 0; i < whiteListUrls.length; i++) {
                        if (window.location.href.indexOf(whiteListUrls[i]) > -1) {
                            !this.trackingInitialized ? this.initTracking() : null;
                        }
                    }
                }
            });

        this.document.body.classList.add(this.brand);
        this.document.querySelector<HTMLLinkElement>('link[ rel *= "icon"]').href =
            'assets/' + this.brand + '/favicon.png?v=6';
        this.setTitle(this.brandTitle);
        // Subscribe to config changes
        this._layoutConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.layoutConfig = config;

                if (this.layoutConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }
            });

        const requestIdle = (window as any)?.requestIdleCallback;
        if (typeof requestIdle === 'function') {
            requestIdle(() => {
                this.browserTestService.checkCompatibility();
            });
        } else {
            setTimeout(() => {
                this.browserTestService.checkCompatibility();
            }, 0);
        }


        // La vérification initiale de l'authentification et la gestion de l'état
        // (y compris l'erreur 401 initiale) sont maintenant entièrement gérées
        // par AuthenticationService.
        // AppComponent réagit aux changements d'état via son abonnement
        // au 'userData' et 'doLogout' du communicationCenter (voir lignes 167-178).
        // La suppression de ce bloc évite un appel /users/me redondant.

        this.enableFreemiumPremiumFeature();

        // Config du moteur de recherche d'activité
        this.communicationCenter.getRoom('activity-search-engine').next('config', {
            flattenSubLesson: true
        });

        // Config editeur de parcours & d'activité
        this.lessonEditionConfig.disallowTypology(TypologyLabel.external)
        this.activityEditionConfig.disallowTypology(TypologyLabel.external)
    }

    ngAfterViewInit(): void {
        // because of default loader fuse we need to hide it t avoid blue screen on posthog
        const splash = document.getElementById('fuse-splash-screen');
        if (splash) {
            splash.style.transition = 'opacity 0.5s';
            splash.style.opacity = '0';
            setTimeout(() => splash.remove(), 500);
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    public setTitle(newTitle: string): void {
        this.titleService.setTitle(newTitle);
    }

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    loadGoogleAnalytics(trackingID: string): void {
        const gaScript = document.createElement('script');
        gaScript.setAttribute('async', 'true');
        gaScript.setAttribute(
            'src',
            `https://www.googletagmanager.com/gtag/js?id=${trackingID}`
        );

        const gaScript2 = document.createElement('script');
        gaScript2.innerText = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${trackingID}\', {'anonymize_ip': true, 'cookie_expires': 33696000});`;

        document.documentElement.firstChild.appendChild(gaScript);
        document.documentElement.firstChild.appendChild(gaScript2);
    }

    loadMatomo(trackingID: string): void {
        const matomoScript = document.createElement('script');
        matomoScript.setAttribute('type', `text/javascript`);
        matomoScript.innerText = `var _paq = window._paq || [];_paq.push([\'enableLinkTracking\']);(function() {var u=\'https://matomo.tralalere.com/\';_paq.push([\'setTrackerUrl\', u+\'matomo.php\']);_paq.push([\'setSiteId\', \'${trackingID}\']);var d=document, g=d.createElement(\'script\'), s=d.getElementsByTagName(\'script\')[0];g.type=\'text/javascript\'; g.async=true; g.defer=true; g.src=u+\'matomo.js\'; s.parentNode.insertBefore(g,s);})();`;
        document.documentElement.firstChild.appendChild(matomoScript);
    }

    loadTartaucitron(): HTMLScriptElement {
        const tarteaucitronScript = document.createElement('script');
        tarteaucitronScript.setAttribute(
            'src',
            `https://www.tralalere.com/header/tarteaucitron/gcmv2/tarteaucitron.min.js`
        );
        this.renderer.appendChild(document.body, tarteaucitronScript);
        return tarteaucitronScript;
    }

    tarteaucitronInit(): void {
        window.tarteaucitronForceLanguage = this._translateService.currentLang;
        tarteaucitron.init(this.tarteaucitronConf);
        // Google analytics
        if (this.GATracking) {
            tarteaucitron.user.gtagUa = this.trackingID;
            // tarteaucitron.user.gtagCrossdomain = ['example.com', 'example2.com'];
            tarteaucitron.user.gtagMore = function (): void {
                /* add here your optionnal gtag() */
            };
            (tarteaucitron.job = tarteaucitron.job || []).push('gtag');
        }

        // matomo
        /* // remove from tarteaucitron to force matomo tracking
        if (this.matomoTracking) {
            tarteaucitron.user.matomoId = this.matomoTrackingID;
            tarteaucitron.user.matomoHost = 'https://matomo.tralalere.com/';
            (tarteaucitron.job = tarteaucitron.job || []).push('matomo');
        } */

        // youtube
        if (this.youTubeTracking) {
            (tarteaucitron.job = tarteaucitron.job || []).push('youtube');
        }

        // twitter
        if (this.twitterTracking) {
            (tarteaucitron.job = tarteaucitron.job || []).push('twitterembed');
        }

        // GAds
        if (this.GAdsTracking) {
            tarteaucitron.user.googleadsId = this.GAdsTrackingID;
            (tarteaucitron.job = tarteaucitron.job || []).push('googleads');
        }

        // GTM
        if (this.GTMTracking) {
            tarteaucitron.user.googletagmanagerId = this.GTMTrackingID;
            (tarteaucitron.job = tarteaucitron.job || []).push('googletagmanager');
        }

        // Meta Pixel
        if (this.MetaPixelTracking) {
            tarteaucitron.user.facebookpixelId = this.MetaPixelTrackingID;
            tarteaucitron.user.facebookpixelMore = function () { /* add here your optionnal facebook pixel function */
            };
            (tarteaucitron.job = tarteaucitron.job || []).push('facebookpixel');
        }

        // Snapchat
        if (this.SnapchatTracking) {
            tarteaucitron.user.snapchatId = this.SnapchatTrackingID;
            tarteaucitron.user.snapchatEmail = '__INSERT_USER_EMAIL__';
            (tarteaucitron.job = tarteaucitron.job || []).push('snapchat');
        }

        //GCMv2
        if (this.GTMTracking || this.GAdsTracking || this.GATracking) {
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmadstorage');
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmanalyticsstorage');
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmfunctionality');
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmpersonalization');
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmadsuserdata');
            (tarteaucitron.job = tarteaucitron.job || []).push('gcmsecurity');
        }

        this.trackingInitialized = true;
    }

    navigateToAnchor(event: Event) {
        event.preventDefault();
        const anchorId = 'maincontent';
        const element = document.getElementById(anchorId);

        if (element) {
            element.focus();
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    private initLayoutConfigManagement(): void {
        if (customLayoutConfig.hasOwnProperty('default')) {
            if (customLayoutConfig.hasOwnProperty(this.authService.accessLevel)) {
                this._layoutConfigService.setConfig(customLayoutConfig[this.authService.accessLevel], {emitEvent: true});
            } else {
                this._layoutConfigService.setConfig(customLayoutConfig['default'], {emitEvent: true});
            }

            this.communicationCenter
                .getRoom('authentication')
                .getSubject('userData')
                .subscribe(userData => {
                    const role = this.authService.accessLevel;
                    if (customLayoutConfig.hasOwnProperty(role)) {
                        this._layoutConfigService.setConfig(customLayoutConfig[role], {emitEvent: true});
                    } else {
                        this._layoutConfigService.setConfig(customLayoutConfig['default'], {emitEvent: true});
                    }
                });
        }
    }

    private initMobilePlatformManagement(): void {
        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }
    }

    private initTracking(): void {

        // cookies managment with tarteaucitron
        if ((this.youTubeTracking
                || this.twitterTracking
                || this.GATracking
                || this.GAdsTracking
                || this.GTMTracking
                || this.MetaPixelTracking
                || this.SnapchatTracking)
            && !this.authService.isGAR()
        ) {
            this.loadTartaucitron().onload = () => {
                this.tarteaucitronInit();
            };
        }

        // Matomo tracking
        if (this.matomoTracking) {
            this.loadMatomo(this.matomoTrackingID);
            this.angularticsMatomo.startTracking();
        }

        // Google Analytics tracking
        if (this.GATracking) {
            this.angularticsGA.startTracking();
        }
    }

    private initLangManagement(): void {
        if (this.globalConfigurationService.getUseSelectedLangAsDefault()) {
            const defaultLang: string[] = langs.map((lang) => lang.id);
            this.browserLang =
                defaultLang.indexOf(navigator.language.substring(0, 2)) !== -1
                    ? navigator.language.substring(0, 2)
                    : defaultLanguage;

            // Add languages
            this._translateService.addLangs(defaultLang);

            // Set the default language
            this._translateService.setDefaultLang(this.browserLang);

            // Use a language
            this.lang = localStorage.getItem('lang') || this.browserLang;
            this.document.documentElement.lang = this.lang;
            this.connector.setLanguage(this.lang);
            this._translateService.use(this.lang);
            // Set the navigation translations
            this.translationLoader.loadTranslations(...defaultLang);
        } else {
            const lang = this.languageService.getActiveLanguage().id;
            this.document.documentElement.lang = lang;
            this.connector.setLanguage(lang);
            // Set the navigation translations
            this.translationLoader.loadTranslations(...this.languageService.availableLanguages.map((lang) => lang.id));
        }

        this._translateService.onLangChange.subscribe(
            (event: LangChangeEvent) => {
                this.connector.setLanguage(event.lang);
                this.document.documentElement.lang = event.lang;
                this._translateService.currentLang = event.lang;
                // init tarteaucitron and update tarteaucitron language
                if (
                    this.youTubeTracking ||
                    this.twitterTracking ||
                    this.GATracking
                ) {
                    this.tarteaucitronInit();
                }
            }
        );
        if (this.globalConfigurationService.getsubscribeToLanguageChange()) {
            this.checkIfQueryParamsForceLanguage();
            this.languageService.onLanguageChange$.subscribe((lang) => {
                // check route with access settings for new language
                this.isRouteAccessible.checkRouteAllowedOrRedirect(this.route.snapshot, this.router.routerState.snapshot);
            });
        }
    }

    private initNavigation(): void {
        // Get default navigation
        this.navigation = navigation;

        // Register the navigation to the service
        this._fuseNavigationService.register('main', []);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const slugs = event.url.split('/').slice(1);

                const title = slugs
                    .map((slug) => `${capitalize(slug.split('?')[0])}`)
                    .join(' - ');
                this.setTitle(`${this.brandTitle} | ${title}`);
                this.isAllowedSkipNavigation = !(event.url.includes('register') || event.url.includes('login') || event.url.includes('password'));
            });
    }

    private initBodyClassManagement(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.route),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                map((route) => route.snapshot.data?.['menu'] ?? 'level0'),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((menuId) => this.bodyClassService.switchClassPath(menuId));
    }

    private postAuthentication(userData: UserDataEntity): void {
        // gift get level and send to header
        this.connector.loadCollection('customer-success/lesson-streak')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((data: any) => {
                if (data && data?.entities.length > 0 && data?.entities[0].get('progress')?.completed) {
                    this.communicationCenter
                        .getRoom('headerMenu')
                        .getSubject('gift').next(data?.entities[0].get('progress')?.completed);
                }
            });

        this.isSnackbarRecallButtonConfigActive = this.snackbarNews.isRecallButtonActive;

        timer(1000).subscribe(() => {
            // isMobileAlert is used to show a msg mobile not supported but without redirection
            if (isMobile() && (mobileUrlRedirection || isMobileAlert)) {
                const dialogRef = this.dialog.open(
                    MobileRedirectionModalComponent,
                    {}
                );
                dialogRef.afterClosed().subscribe((result) => {
                    // isMobileAlert => no redirection only alert
                    if (result && !isMobileAlert) {
                        window.location.href = mobileUrlRedirection;
                    }
                });
            }
        });

        this.updateFreemiumPremiumMode(userData);
        this.setMenuForCurrentUser(userData);
    }

    private triggerFreemiumTrialModal() {
        this.licensesService.getAllLicenses(true)
            .pipe(
                take(1),
                tap((licenses) => {
                    if (licenses.length === 0) {
                        this.communicationCenter
                            .getRoom('freemium-premium')
                            .getSubject('access-premium-display-reverse-trial-modal')
                            .next(true);
                    }
                })
            ).subscribe()
    }

    private postLogout(): void {
        this.isSnackbarRecallButtonConfigActive = false;

        this.updateFreemiumPremiumMode(null);
        this.navigationService.setMenu('guest');
    }

    private checkIfQueryParamsForceLanguage(): void {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            const queryParams = this.router.parseUrl(this.router.url).queryParams;
            const suggestedLanguageId = queryParams && queryParams['SuggestedLanguage'];

            // if there is no our query param, we don't need to do anything
            if (!suggestedLanguageId) {
                return;
            }
            const suggestedLanguage = this.languageService.availableLanguages.find(lang => lang.id === suggestedLanguageId);

            // if the language is already set, we don't need to do anything
            if (this.languageService.getActiveLanguage() === suggestedLanguage) {
                return;
            }
            this.languageService.setActiveLanguage(suggestedLanguage);

            // and now we remove the suggested language from the query params
            this.router.navigate([], {
                queryParams: {...queryParams, SuggestedLanguage: null},
                queryParamsHandling: 'merge'
            });
        });
    }

    private enableFreemiumPremiumFeature() {
        // Action à faire quand l'utilisateur freemium n'est pas autorisé à accéder à une page premium
        this.freemiumPremiumService.onUnauthorizedAccess().subscribe((url) => {
            this.eventService.trackEvent('open modal', 'item ' + url, 'premium');
            this.dialog.open(FreemiumPremiumModalComponent, {
                panelClass: 'freemium-premium-modal-wrapper',
                data: {attemptedUrl: url}
            });
        });

        // On ajoute un guard pour empêcher l'accès aux routes non autorisées pour les utilisateurs freemium
        const addGuard = (route: Route) =>
         {
            route.canActivate = route.canActivate
                ? [isAuthorizedByFreemiumPremiumGuard].concat(<any> route.canActivate)
                : [isAuthorizedByFreemiumPremiumGuard];
            route.canActivateChild = route.canActivateChild
                ? [isAuthorizedByFreemiumPremiumGuard].concat(<any> route.canActivateChild)
                : [isAuthorizedByFreemiumPremiumGuard];
            route.runGuardsAndResolvers = 'pathParamsOrQueryParamsChange'; // on trigger le guard aux changements des query params aussi (pour les différentes pas de listing de lessons)
        };

        this.router.config
            .filter(route => !route.data || !route.data.skipGuard)
            .forEach(route => addGuard(route));

        // on active le mode freemium avec les routes autorisées
        this.updateFreemiumPremiumMode();
    }

    private updateFreemiumPremiumMode(userData?: UserDataEntity, forceToLoadLicenses = false): void {
        if (!userData || !this.authService.isAtLeastTrainer()) {
            this.freemiumPremiumService.setMode('disabled');
        } else if (this.authService.isGAR()) {
            this.freemiumPremiumService.setMode('disabled');
        } else {
            this.licensesService.getAllLicenses(forceToLoadLicenses)
                .pipe(take(1))
                .subscribe((allLicenses) => {
                    const now = new Date();
                    const activeLicenses = allLicenses.filter((license) => {
                        const expirationDate = new Date(license.attributes.expirationDate * 1000);
                        return expirationDate > now;
                    });
                    const hasAnyLicenseHistory = allLicenses.length > 0;

                    if (activeLicenses.length > 0) {
                        const expirationTimestamp = activeLicenses[0].attributes.expirationDate;
                        const expirationDate = new Date(expirationTimestamp * 1000);
                        const isDemo = activeLicenses[0].attributes?.isDemo;
                        this.freemiumPremiumService.setMode('premium', {
                            expirationDate,
                            hasAnyLicenseHistory,
                            isDemo
                        });
                        this.navigationService.addMenuBuilderMiddleware('freemiumPremiumMiddleware', (anyMenu) => {
                            // on supprime le middleware pour ne pas indiquer les routes premium
                            return anyMenu;
                        });
                    } else {
                        const latestLicenseExpirationTimestamp = hasAnyLicenseHistory
                            ? Math.max(...allLicenses.map((license) => license.attributes.expirationDate))
                            : null;
                        this.freemiumPremiumService.setMode('freemium', {
                            pathList: {whitelist: FreemiumWhitelistRoutes},
                            hasAnyLicenseHistory,
                            expirationDate: latestLicenseExpirationTimestamp
                                ? new Date(latestLicenseExpirationTimestamp * 1000)
                                : null,
                            isDemo: true
                        });

                        this.navigationService.addMenuBuilderMiddleware('freemiumPremiumMiddleware', (menu) => {
                            const isFreemiumAllowed = (link: AnyLinkType) => {
                                if ('link' in link) {
                                    const linkUrl = link.link;
                                    const queryParamIndex = link.queryParams ?? {};
                                    const queryString = Object.keys(queryParamIndex).length > 0
                                        ? '?' + Object.entries(queryParamIndex).map(([key, value]) => `${key}=${value}`).join('&')
                                        : '';
                                    const fullLink = linkUrl + queryString;
                                    return this.freemiumPremiumService.isRouteAuthorized(fullLink);

                                }

                                return true; // si c'est un groupe, on le garde
                            };

                            const applyRecursively = (items: AnyLinkType[]) => {
                                items.map(item => {
                                    if ('links' in item) {
                                        item.links = applyRecursively(item.links);
                                    }

                                    const isAllowed = isFreemiumAllowed(item);
                                    if (!isAllowed && 'icon' in item) {
                                        item.icon = 'navigation-link__premium-item';

                                        // if item has an action, override it to open the freemium premium modal
                                        if ('action' in item) {
                                            item.link = '#' + item.label; // remove url to avoid navigation
                                            item.action = ($event) => {
                                                $event.preventDefault();
                                                $event.stopPropagation();
                                                // manually trigger the unauthorized access handling
                                                this.freemiumPremiumService.forceTriggerUnauthorizedAccess(item.link);
                                            };
                                        }
                                    }
                                });

                                return items;
                            };

                            return applyRecursively(menu);
                        });

                        // On test la route courante, au cas où l'utilisateur se serait loggé sur une page premium
                        if (!this.freemiumPremiumService.isRouteAuthorized(this.location.path())) {
                            this.router.navigate(['/']);
                        }
                    }
                });

        }

    }

    private setMenuForCurrentUser(userData: UserDataEntity): void {
        let menu = 'guest';

        if (!!userData) {
            if (this.authService.isAtLeastTrainer()) {
                menu = 'trainer';
            } else if (this.authService.isAtLeastLearner()) {
                menu = 'learner';
            }
        }

        this.navigationService.setMenu(menu);
    }
}